import { inject, Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import {
  DocumentData,
  FirestoreAdapter,
  QueryFn,
} from '@shared/firestore/firestore-adapter';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import {
  DEFAULT_AI_CONCIERGE_SETTINGS,
  IAiConciergeSettings,
  IConciergeSignupPublic,
} from '../models/IAiConciergeSettings';
import { ILocation } from '../../models/ILocation';
import type {
  IVisitFlowsConfig,
  IVisitFlowsConfigDoc,
} from '../models/IVisitFlowsConfig';
import { SessionService } from '../../services/session.service';
import { SharedService } from '../../services/shared.service';
import { IIncentive, IncentivePurposeTypeEnum } from '../../models/IIncentives';
import { LoyaltySelectEnum } from '../../enums/loyalty-select.enum';
import { TriggerType } from '../../shared/enums/TriggerType';
import { IOffering } from '../models/IOffering';
import {
  EngagementTriggerType,
  IEngagementRule,
  RuleSentiment,
} from '../models/IEngagementRule';
import { ENROLLMENT_REWARD_GOAL } from './engagement-rules.service';

export interface IncentiveMigrationEntry {
  incentiveId: string;
  incentiveName: string;
  offeringId?: string;
  ruleId?: string | null;
  additionalRuleIds?: string[];
  triggerType?: string;
  visitNumber?: number;
  amountSpentThreshold?: number;
  referralCount?: number;
  recurring?: boolean;
  reason?: string;
  note?: string;
  /** AI confidence score (0–1) for this mapping. Null if fallback was used. */
  confidence?: number | null;
  /** AI reasoning explaining why this mapping was chosen. */
  reasoning?: string | null;
}

export interface IncentiveMigrationResult {
  migrated: IncentiveMigrationEntry[];
  skipped: IncentiveMigrationEntry[];
  alreadyMigrated: IncentiveMigrationEntry[];
  possibleDuplicates: IncentiveMigrationEntry[];
  summary: {
    total: number;
    migrated: number;
    skipped: number;
    alreadyMigrated: number;
    possibleDuplicates: number;
  };
}

type LegacyIncentiveRuleMapping = {
  triggerType: EngagementTriggerType;
  goal: string;
  sentiment: RuleSentiment;
  priority: number;
  confidence: number;
  reasoning: string;
  visitNumber?: number;
  amountSpentThreshold?: number;
  referralCount?: number;
  recurring?: boolean;
};

type MigratedRuleResult = {
  ruleId: string;
  reusedExistingRule: boolean;
};

type StarterLegacyOfferingMap = Partial<
  Record<
    'POST_VISIT_NEUTRAL' | 'POST_VISIT_UNHAPPY' | 'ENROLLMENT_REWARD',
    string
  >
>;

type StarterRuleIdMap = Partial<Record<EngagementTriggerType, string>>;

const AI_SETTINGS_DOC_ID = 'default';

/** Firestore path for global visit flows config (legacy vs AI). */
const VISIT_FLOWS_CONFIG_PATH = 'config/visitFlows';

const DEFAULT_VISIT_FLOWS_CONFIG: IVisitFlowsConfig = {
  legacy: true,
  ai: false,
};

@Injectable({
  providedIn: 'root',
})
export class AiConciergeService {
  private firestore = inject(FirestoreAdapter);
  private functions = inject(AngularFireFunctions);
  private sessionService = inject(SessionService);
  private sharedService = inject(SharedService);

  private assertSupportUserForAiToggle(action: string): void {
    if (
      !this.sessionService.isAdmin() &&
      !this.sessionService.isSupportUser()
    ) {
      throw new Error(
        `Only admins or support users can ${action}. Contact Get Kudos support for help.`,
      );
    }
  }

  /**
   * Get AI Concierge settings for a location.
   * Uses path: locations/{locationId}/aiSettings/default
   */
  getSettings(locationId: string): Observable<IAiConciergeSettings | null> {
    if (!locationId) return of(null);
    return this.firestore
      .collection('locations')
      .doc(locationId)
      .collection('aiSettings')
      .doc(AI_SETTINGS_DOC_ID)
      .get()
      .pipe(
        map((snap) => {
          if (!snap.exists) return null;
          return {
            id: snap.id,
            ...snap.data(),
          } as unknown as IAiConciergeSettings;
        }),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'getting AI Concierge settings',
            'AiConciergeService',
          );
          return of(null);
        }),
      );
  }

  /**
   * Get settings as valueChanges for reactive binding.
   */
  getSettings$(locationId: string): Observable<IAiConciergeSettings | null> {
    if (!locationId) return of(null);
    return this.firestore
      .collection('locations')
      .doc(locationId)
      .collection('aiSettings')
      .doc(AI_SETTINGS_DOC_ID)
      .valueChanges()
      .pipe(
        map((data) => (data ? (data as IAiConciergeSettings) : null)),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'subscribing to AI Concierge settings',
            'AiConciergeService',
          );
          return of(null);
        }),
      );
  }

  /**
   * Public-safe sign-up config for the unauthenticated sign-up pages, read from
   * the world-readable location doc. The `aiSettings` doc is restricted to
   * admins/managers, so a Cloud Function mirrors these fields onto the location
   * doc (`conciergeSignupPublic`).
   */
  getPublicSignupConfig$(
    locationId: string,
  ): Observable<IConciergeSignupPublic | null> {
    if (!locationId) return of(null);
    return this.firestore
      .collection('locations')
      .doc(locationId)
      .get()
      .pipe(
        map((snap) => {
          if (!snap.exists) return null;
          const data = snap.data() as ILocation | undefined;
          return data?.conciergeSignupPublic ?? null;
        }),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'getting public concierge sign-up config',
            'AiConciergeService',
          );
          return of(null);
        }),
      );
  }

  /**
   * Update AI Concierge settings (partial update).
   * Creates the document with defaults if it does not exist.
   */
  async updateSettings(
    locationId: string,
    partial: Partial<IAiConciergeSettings>,
  ): Promise<void> {
    if (!locationId) return;
    if ('enabled' in partial) {
      this.assertSupportUserForAiToggle('change AI Concierge enabled state');
    }
    const ref = this.firestore
      .collection('locations')
      .doc(locationId)
      .collection('aiSettings')
      .doc(AI_SETTINGS_DOC_ID);
    const snap = await ref.get().toPromise();
    const payload = snap?.exists
      ? { ...partial }
      : { ...DEFAULT_AI_CONCIERGE_SETTINGS, ...partial };
    await ref.set(payload, { merge: true });
  }

  /**
   * Get the global visit flows config (legacy vs AI). Used by the dashboard to show and toggle which flow runs on visit/loyaltyVisit creation.
   */
  getVisitFlowsConfig(): Observable<IVisitFlowsConfig> {
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    return this.firestore
      .collection(collection)
      .doc(docId)
      .get()
      .pipe(
        map((snap) => {
          if (!snap.exists) return DEFAULT_VISIT_FLOWS_CONFIG;
          const data = snap.data() as Partial<IVisitFlowsConfig>;
          return {
            legacy: data?.legacy !== false,
            ai: data?.ai === true,
          };
        }),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'getting visit flows config',
            'AiConciergeService',
          );
          return of(DEFAULT_VISIT_FLOWS_CONFIG);
        }),
      );
  }

  /**
   * Subscribe to visit flows config for reactive UI (e.g. dashboard toggle).
   */
  getVisitFlowsConfig$(): Observable<IVisitFlowsConfig> {
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    return this.firestore
      .collection(collection)
      .doc(docId)
      .valueChanges()
      .pipe(
        map((data) => {
          const d = data as Partial<IVisitFlowsConfig> | undefined;
          if (!d) return DEFAULT_VISIT_FLOWS_CONFIG;
          return {
            legacy: d.legacy !== false,
            ai: d.ai === true,
          };
        }),
        startWith(DEFAULT_VISIT_FLOWS_CONFIG),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'subscribing to visit flows config',
            'AiConciergeService',
          );
          return of(DEFAULT_VISIT_FLOWS_CONFIG);
        }),
      );
  }

  /**
   * Set the global visit flows config. Toggling here lets testers switch between legacy and AI flow without redeploying functions.
   */
  async setVisitFlowsConfig(
    partial: Partial<IVisitFlowsConfig>,
  ): Promise<void> {
    this.assertSupportUserForAiToggle('change visit flow config');
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    const ref = this.firestore.collection(collection).doc(docId);
    await ref.set(partial, { merge: true });
  }

  /**
   * Set visit flows config for a specific location via the locationOverrides map.
   * Uses update() so dot-notation keys are interpreted as nested field paths.
   * Falls back to set() with a properly nested object when the document doesn't exist yet.
   */
  async setVisitFlowsConfigForLocation(
    locationId: string,
    config: IVisitFlowsConfig,
  ): Promise<void> {
    this.assertSupportUserForAiToggle('activate or deactivate AI Concierge');
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    const ref = this.firestore.collection(collection).doc(docId);
    try {
      await ref.update({ [`locationOverrides.${locationId}`]: config });
    } catch {
      await ref.set({ locationOverrides: { [locationId]: config } });
    }
  }

  /** Full visit-flows document (for resolving per-location AI flag on dashboard). */
  getVisitFlowsDocument$(): Observable<IVisitFlowsConfigDoc | null> {
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    return this.firestore
      .collection(collection)
      .doc(docId)
      .valueChanges()
      .pipe(
        map((doc) => (doc as IVisitFlowsConfigDoc | undefined | null) ?? null),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'subscribing to visit flows document',
            'AiConciergeService',
          );
          return of(null);
        }),
      );
  }

  /** True when AI Concierge visit flow is enabled for this location (after overrides). */
  isAiFlowEnabledForLocation(
    doc: IVisitFlowsConfigDoc | null | undefined,
    locationId: string,
  ): boolean {
    if (!locationId || !doc) return false;
    const override = doc.locationOverrides?.[locationId];
    if (override != null) {
      return override.ai === true;
    }
    return doc.ai === true;
  }

  /**
   * Get the visit flows config for a specific location.
   * Checks locationOverrides first, then falls back to global defaults.
   */
  getVisitFlowsConfigForLocation(
    locationId: string,
  ): Observable<IVisitFlowsConfig> {
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    return this.firestore
      .collection(collection)
      .doc(docId)
      .valueChanges()
      .pipe(
        map((data: any) => {
          if (!data) return DEFAULT_VISIT_FLOWS_CONFIG;
          const override = data?.locationOverrides?.[locationId];
          if (override) {
            return {
              legacy: override.legacy !== false,
              ai: override.ai === true,
            };
          }
          return {
            legacy: data.legacy !== false,
            ai: data.ai === true,
          };
        }),
        startWith(DEFAULT_VISIT_FLOWS_CONFIG),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'getting location visit flows config',
            'AiConciergeService',
          );
          return of(DEFAULT_VISIT_FLOWS_CONFIG);
        }),
      );
  }

  /**
   * One-shot read of visit flows config for a specific location (uses .get(), not .valueChanges()).
   * Suitable for forkJoin / one-time reads where the caller needs a completing Observable.
   */
  getVisitFlowsConfigForLocationOnce(
    locationId: string,
  ): Observable<IVisitFlowsConfig> {
    const [collection, docId] = VISIT_FLOWS_CONFIG_PATH.split('/');
    return this.firestore
      .collection(collection)
      .doc(docId)
      .get()
      .pipe(
        map((snap) => {
          if (!snap.exists) return DEFAULT_VISIT_FLOWS_CONFIG;
          const data = snap.data() as any;
          const override = data?.locationOverrides?.[locationId];
          if (override) {
            return {
              legacy: override.legacy !== false,
              ai: override.ai === true,
            };
          }
          return {
            legacy: data?.legacy !== false,
            ai: data?.ai === true,
          };
        }),
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'getting location visit flows config (once)',
            'AiConciergeService',
          );
          return of(DEFAULT_VISIT_FLOWS_CONFIG);
        }),
      );
  }

  /**
   * Load AI settings and the authoritative visit-flows AI flag for a location in a single call.
   * The returned settings object has its `enabled` field set to `visitFlows.ai` so the
   * toggle reflects the same source of truth the backend triggers use.
   */
  getSettingsWithFlowState(locationId: string): Observable<{
    settings: Partial<IAiConciergeSettings>;
    aiEnabled: boolean;
  }> {
    if (!locationId) {
      return of({ settings: DEFAULT_AI_CONCIERGE_SETTINGS, aiEnabled: false });
    }
    return forkJoin({
      settings: this.getSettings(locationId),
      flows: this.getVisitFlowsConfigForLocationOnce(locationId),
    }).pipe(
      map(({ settings, flows }) => ({
        settings: (settings ??
          DEFAULT_AI_CONCIERGE_SETTINGS) as Partial<IAiConciergeSettings>,
        aiEnabled: flows.ai,
      })),
      catchError(() =>
        of({
          settings:
            DEFAULT_AI_CONCIERGE_SETTINGS as Partial<IAiConciergeSettings>,
          aiEnabled: false,
        }),
      ),
    );
  }

  /**
   * Trigger the Concierge sign-up flow after the sign-up form has created
   * customer and visit. Sends the initial "How was your visit?" SMS and
   * creates the conversation. Call this from the contact/sign-up page after
   * saveVisit when AI Concierge is enabled for the location.
   */
  async triggerSignUp(
    customerId: string,
    locationId: string,
    visitId?: string,
  ): Promise<{ ok: boolean; skipped?: string; conversationId?: string }> {
    const callable = this.functions.httpsCallable<
      { customerId: string; locationId: string; visitId?: string },
      { ok: boolean; skipped?: string; conversationId?: string }
    >('onSignUpSubmitted');
    return firstValueFrom(
      callable({ customerId, locationId, visitId }).pipe(
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'triggering Concierge sign-up',
            'AiConciergeService',
          );
          throw err;
        }),
      ),
    );
  }

  /**
   * Migrate legacy incentives to AI Concierge offerings and engagement rules
   * for a single location. Idempotent — skips already-migrated incentives.
   */
  async migrateIncentives(
    locationId: string,
  ): Promise<IncentiveMigrationResult> {
    try {
      return await this.migrateIncentivesLocally(locationId);
    } catch (err) {
      this.sharedService.logErrors(
        err,
        'migrating incentives',
        'AiConciergeService',
      );
      throw err;
    }
  }

  async createStarterRules(locationId: string): Promise<number> {
    const existingRules = await this.listRulesForMigration(locationId);
    const starterRuleIds = await this.createStarterRuleSet(locationId, {
      existingRules,
    });
    return Object.keys(starterRuleIds).length;
  }

  async createStarterRulesFromLegacy(locationId: string): Promise<number> {
    const [legacyIncentives, existingOfferings, existingRules] =
      await Promise.all([
        this.listLegacyIncentives(locationId).catch((err) => {
          this.sharedService.logErrors(
            err,
            'loading legacy incentives for starter rules',
            'AiConciergeService',
          );
          return [] as IIncentive[];
        }),
        this.listOfferingsForMigration(locationId),
        this.listRulesForMigration(locationId),
      ]);

    const starterRuleIds = await this.createStarterRuleSet(locationId, {
      existingRules,
    });
    const offeringsByLegacyId = new Map(
      existingOfferings
        .map((offering) => [
          this.migratedFromIncentiveIdForOffering(offering),
          offering.id,
        ])
        .filter(
          (entry): entry is [string, string] =>
            typeof entry[0] === 'string' && typeof entry[1] === 'string',
        ),
    );
    const offeringsByName = new Map(
      existingOfferings
        .filter((offering) => offering.id)
        .map((offering) => [
          this.normalizeName(offering.name),
          offering.id as string,
        ]),
    );

    const matches: Array<{
      triggerType: keyof StarterLegacyOfferingMap;
      incentive: IIncentive | null;
    }> = [
      {
        triggerType: 'POST_VISIT_NEUTRAL',
        incentive: this.findStarterLegacyIncentive(
          legacyIncentives,
          'POST_VISIT_NEUTRAL',
        ),
      },
      {
        triggerType: 'POST_VISIT_UNHAPPY',
        incentive: this.findStarterLegacyIncentive(
          legacyIncentives,
          'POST_VISIT_UNHAPPY',
        ),
      },
      {
        triggerType: 'ENROLLMENT_REWARD',
        incentive: this.findStarterLegacyIncentive(
          legacyIncentives,
          'ENROLLMENT_REWARD',
        ),
      },
    ];

    for (const { triggerType, incentive } of matches) {
      const ruleId = starterRuleIds[triggerType];
      if (!ruleId || !incentive?.id) continue;
      const offeringId = await this.getOrCreateStarterOffering(
        locationId,
        incentive,
        offeringsByLegacyId,
        offeringsByName,
      ).catch((err) => {
        this.sharedService.logErrors(
          err,
          `copying legacy incentive for ${triggerType}`,
          'AiConciergeService',
        );
        return null;
      });
      if (offeringId) {
        await this.attachStarterOfferingToRule(
          locationId,
          ruleId,
          offeringId,
          incentive.id,
        );
      }
    }

    return Object.keys(starterRuleIds).length;
  }

  private async migrateIncentivesLocally(
    locationId: string,
  ): Promise<IncentiveMigrationResult> {
    const [legacyIncentives, existingOfferings, existingRules] =
      await Promise.all([
        this.listLegacyIncentives(locationId),
        this.listOfferingsForMigration(locationId),
        this.listRulesForMigration(locationId),
      ]);

    const migrated: IncentiveMigrationEntry[] = [];
    const skipped: IncentiveMigrationEntry[] = [];
    const alreadyMigrated: IncentiveMigrationEntry[] = [];
    const possibleDuplicates: IncentiveMigrationEntry[] = [];
    const existingOfferingIdByLegacyId = new Map(
      existingOfferings
        .map((offering) => [
          this.migratedFromIncentiveIdForOffering(offering),
          offering.id,
        ])
        .filter(
          (entry): entry is [string, string] =>
            typeof entry[0] === 'string' && typeof entry[1] === 'string',
        ),
    );
    const existingOfferingIdByName = new Map(
      existingOfferings
        .filter((offering) => offering.id)
        .map((offering) => [
          this.normalizeName(offering.name),
          offering.id as string,
        ]),
    );

    for (const incentive of legacyIncentives) {
      if (!incentive.id) continue;
      const migrationEntry = this.migrationEntry(incentive);

      try {
        const mapping = this.mapLegacyIncentiveToRule(incentive);
        if (!mapping) {
          skipped.push({
            ...migrationEntry,
            reason:
              'No safe built-in AI Concierge trigger matches this legacy incentive.',
          });
          continue;
        }

        const normalizedName = this.normalizeName(incentive.name);
        const offeringId =
          existingOfferingIdByLegacyId.get(incentive.id) ??
          existingOfferingIdByName.get(normalizedName) ??
          (await this.createMigratedOffering(locationId, incentive));
        const ruleResult = await this.createOrUpdateMigratedRule(
          locationId,
          incentive,
          offeringId,
          mapping,
          existingRules,
        );

        migrated.push({
          ...migrationEntry,
          offeringId,
          ruleId: ruleResult.ruleId,
          triggerType: mapping.triggerType,
          visitNumber: mapping.visitNumber,
          amountSpentThreshold: mapping.amountSpentThreshold,
          referralCount: mapping.referralCount,
          recurring: mapping.recurring,
          confidence: mapping.confidence,
          reasoning: mapping.reasoning,
          note: ruleResult.reusedExistingRule
            ? 'Updated an existing inactive template rule. Legacy incentive was not changed.'
            : 'Created inactive for review. Legacy incentive was not changed.',
        });
        existingOfferingIdByLegacyId.set(incentive.id, offeringId);
        existingOfferingIdByName.set(normalizedName, offeringId);
      } catch (err) {
        this.sharedService.logErrors(
          err,
          `migrating legacy incentive ${incentive.id}`,
          'AiConciergeService',
        );
        skipped.push({
          ...migrationEntry,
          reason: 'This incentive could not be copied into AI Concierge.',
        });
      }
    }

    return {
      migrated,
      skipped,
      alreadyMigrated,
      possibleDuplicates,
      summary: {
        total: legacyIncentives.length,
        migrated: migrated.length,
        skipped: skipped.length,
        alreadyMigrated: alreadyMigrated.length,
        possibleDuplicates: possibleDuplicates.length,
      },
    };
  }

  private async listLegacyIncentives(
    locationId: string,
  ): Promise<IIncentive[]> {
    const locationRef = this.firestore.doc(`locations/${locationId}`).ref;
    const query: QueryFn<DocumentData> = (ref) =>
      ref.where('locationId', '==', locationRef);

    const snap = await firstValueFrom(
      this.firestore.collection<IIncentive>('incentives', query).get(),
    );
    return snap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((incentive) => incentive.deleted !== true);
  }

  private async listOfferingsForMigration(
    locationId: string,
  ): Promise<IOffering[]> {
    const snap = await firstValueFrom(
      this.firestore
        .collection('locations')
        .doc(locationId)
        .collection<IOffering>('offerings')
        .get(),
    );
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  private async listRulesForMigration(
    locationId: string,
  ): Promise<IEngagementRule[]> {
    const snap = await firstValueFrom(
      this.firestore
        .collection('locations')
        .doc(locationId)
        .collection<IEngagementRule>('engagementRules')
        .get(),
    );
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  private async createMigratedOffering(
    locationId: string,
    incentive: IIncentive,
  ): Promise<string> {
    const now = new Date().toISOString();
    const offering: Omit<IOffering, 'id'> & Record<string, unknown> = {
      name: incentive.name,
      type: this.isStoreCreditIncentive(incentive) ? 'store_credit' : 'offer',
      amount: this.isStoreCreditIncentive(incentive)
        ? Number(incentive.amount)
        : undefined,
      expiresAfterDays:
        Number(incentive.promoExpiresAfterDays ?? 0) > 0
          ? Number(incentive.promoExpiresAfterDays)
          : 30,
      active: incentive.active !== false,
      limits: this.buildOfferingLimits(incentive),
      migratedFromIncentiveId: incentive.id,
      migratedAt: now,
    };

    const docRef = await this.firestore
      .collection('locations')
      .doc(locationId)
      .collection<IOffering>('offerings')
      .add(this.stripUndefinedDeep(offering) as Partial<IOffering>);
    return docRef.id;
  }

  private async createOrUpdateMigratedRule(
    locationId: string,
    incentive: IIncentive,
    offeringId: string,
    mapping: LegacyIncentiveRuleMapping,
    existingRules: IEngagementRule[],
  ): Promise<MigratedRuleResult> {
    const reusableRule = this.findReusableTemplateRule(mapping, existingRules);
    if (reusableRule?.id) {
      const updatePayload = this.stripUndefinedDeep({
        eligibleOfferIds: [offeringId],
        enabled: false,
        goal: mapping.goal,
        sentiment: mapping.sentiment,
        priority: mapping.priority,
        visitNumber: mapping.visitNumber,
        amountSpentThreshold: mapping.amountSpentThreshold,
        referralCount: mapping.referralCount,
        recurring: mapping.recurring,
        migratedFromIncentiveId: incentive.id,
        migratedAt: new Date().toISOString(),
      }) as Partial<IEngagementRule>;
      await this.firestore
        .collection('locations')
        .doc(locationId)
        .collection<IEngagementRule>('engagementRules')
        .doc(reusableRule.id)
        .update(updatePayload);
      Object.assign(reusableRule, updatePayload);
      return { ruleId: reusableRule.id, reusedExistingRule: true };
    }

    return {
      ruleId: await this.createMigratedRule(
        locationId,
        incentive,
        offeringId,
        mapping,
        existingRules,
      ),
      reusedExistingRule: false,
    };
  }

  private async createMigratedRule(
    locationId: string,
    incentive: IIncentive,
    offeringId: string,
    mapping: LegacyIncentiveRuleMapping,
    existingRules: IEngagementRule[],
  ): Promise<string> {
    const now = new Date().toISOString();
    const rule: Omit<IEngagementRule, 'id'> & Record<string, unknown> = {
      enabled: false,
      triggerType: mapping.triggerType,
      goal: mapping.goal,
      sentiment: mapping.sentiment,
      priority: mapping.priority,
      visitNumber: mapping.visitNumber,
      amountSpentThreshold: mapping.amountSpentThreshold,
      referralCount: mapping.referralCount,
      recurring: mapping.recurring,
      eligibleOfferIds: [offeringId],
      keywords: [],
      timing: { delayHours: 0 },
      migratedFromIncentiveId: incentive.id,
      migratedAt: now,
    };

    const docRef = await this.firestore
      .collection('locations')
      .doc(locationId)
      .collection<IEngagementRule>('engagementRules')
      .add(this.stripUndefinedDeep(rule) as Partial<IEngagementRule>);
    existingRules.push({ id: docRef.id, ...(rule as IEngagementRule) });
    return docRef.id;
  }

  private async createStarterRuleSet(
    locationId: string,
    options: {
      existingRules: IEngagementRule[];
    },
  ): Promise<StarterRuleIdMap> {
    const starterRules: Array<
      Omit<IEngagementRule, 'id'> & { triggerType: EngagementTriggerType }
    > = [
      {
        enabled: true,
        triggerType: 'GOOGLE_REVIEW_DEFAULT',
        goal:
          'When enabled, the AI Concierge will send a Google review request ' +
          'as a separate follow-up message right after the first eligible ' +
          'reply of each visit cycle, in addition to whichever post-visit ' +
          'rule fired. The customer is asked at most once per visit.',
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'POST_VISIT_NEUTRAL',
        goal: 'Ask what could have gone better and show genuine interest in improving',
        sentiment: 'neutral',
        priority: 15,
        keywords: ['okay', 'fine', 'alright', 'average', 'not bad', 'decent'],
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'POST_VISIT_UNHAPPY',
        goal: 'Apologize and offer to make it right',
        sentiment: 'negative',
        priority: 20,
        keywords: [],
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
      {
        enabled: true,
        triggerType: 'ENROLLMENT_REWARD',
        goal: ENROLLMENT_REWARD_GOAL,
        sentiment: 'any',
        priority: 5,
        eligibleOfferIds: [],
        timing: { delayHours: 0 },
      },
    ];

    const ruleIds: StarterRuleIdMap = {};
    for (const rule of starterRules) {
      const existing = options.existingRules.find(
        (item) => item.triggerType === rule.triggerType,
      );
      if (existing?.id) {
        await this.firestore
          .collection('locations')
          .doc(locationId)
          .collection<IEngagementRule>('engagementRules')
          .doc(existing.id)
          .update(this.stripUndefinedDeep(rule) as Partial<IEngagementRule>);
        Object.assign(existing, rule);
        ruleIds[rule.triggerType] = existing.id;
      } else {
        const docRef = await this.firestore
          .collection('locations')
          .doc(locationId)
          .collection<IEngagementRule>('engagementRules')
          .add(this.stripUndefinedDeep(rule) as Partial<IEngagementRule>);
        options.existingRules.push({ id: docRef.id, ...rule });
        ruleIds[rule.triggerType] = docRef.id;
      }
    }
    return ruleIds;
  }

  private async attachStarterOfferingToRule(
    locationId: string,
    ruleId: string,
    offeringId: string,
    incentiveId: string,
  ): Promise<void> {
    await this.firestore
      .collection('locations')
      .doc(locationId)
      .collection<IEngagementRule>('engagementRules')
      .doc(ruleId)
      .update(
        this.stripUndefinedDeep({
          eligibleOfferIds: [offeringId],
          migratedFromIncentiveId: incentiveId,
          migratedAt: new Date().toISOString(),
        }) as Partial<IEngagementRule>,
      );
  }

  private findStarterLegacyIncentive(
    incentives: IIncentive[],
    triggerType: keyof StarterLegacyOfferingMap,
  ): IIncentive | null {
    return (
      incentives.find((incentive) => {
        if (!incentive.id || incentive.appliesTo === 'referrals') return false;
        if (triggerType === 'POST_VISIT_NEUTRAL') {
          return incentive.purpose === IncentivePurposeTypeEnum.retention;
        }
        if (triggerType === 'POST_VISIT_UNHAPPY') {
          return incentive.purpose === IncentivePurposeTypeEnum.recovery;
        }
        return incentive.purpose === IncentivePurposeTypeEnum.enrolledCustomer;
      }) ?? null
    );
  }

  private async getOrCreateStarterOffering(
    locationId: string,
    incentive: IIncentive,
    offeringsByLegacyId: Map<string, string>,
    offeringsByName: Map<string, string>,
  ): Promise<string | null> {
    if (!incentive.id) return null;

    const alreadyMigrated = offeringsByLegacyId.get(incentive.id);
    if (alreadyMigrated) return alreadyMigrated;

    const matchingName = offeringsByName.get(
      this.normalizeName(incentive.name),
    );
    if (matchingName) return matchingName;

    const offeringId = await this.createStarterOffering(locationId, incentive);
    offeringsByLegacyId.set(incentive.id, offeringId);
    offeringsByName.set(this.normalizeName(incentive.name), offeringId);
    return offeringId;
  }

  private async createStarterOffering(
    locationId: string,
    incentive: IIncentive,
  ): Promise<string> {
    const offering: Omit<IOffering, 'id'> & Record<string, unknown> = {
      name: incentive.name,
      type: this.isStoreCreditIncentive(incentive) ? 'store_credit' : 'offer',
      amount: this.isStoreCreditIncentive(incentive)
        ? Number(incentive.amount)
        : undefined,
      expiresAfterDays:
        Number(incentive.promoExpiresAfterDays ?? 0) > 0
          ? Number(incentive.promoExpiresAfterDays)
          : 30,
      active: incentive.active !== false,
      limits: this.buildOfferingLimits(incentive),
      migratedFromIncentiveId: incentive.id,
      migratedAt: new Date().toISOString(),
    };

    const docRef = await this.firestore
      .collection('locations')
      .doc(locationId)
      .collection<IOffering>('offerings')
      .add(this.stripUndefinedDeep(offering) as Partial<IOffering>);
    return docRef.id;
  }

  private findReusableTemplateRule(
    mapping: LegacyIncentiveRuleMapping,
    existingRules: IEngagementRule[],
  ): IEngagementRule | null {
    return (
      existingRules.find((rule) => {
        if (rule.triggerType !== mapping.triggerType) return false;
        if (!this.ruleThresholdsMatch(rule, mapping)) return false;
        return true;
      }) ?? null
    );
  }

  private ruleThresholdsMatch(
    rule: IEngagementRule,
    mapping: LegacyIncentiveRuleMapping,
  ): boolean {
    switch (mapping.triggerType) {
      case 'VISIT_MILESTONE':
        return (
          Number(rule.visitNumber ?? 0) === Number(mapping.visitNumber ?? 0)
        );
      case 'AMOUNT_SPENT':
        return (
          Number(rule.amountSpentThreshold ?? 0) ===
          Number(mapping.amountSpentThreshold ?? 0)
        );
      case 'REFERRAL_MILESTONE':
        return (
          Number(rule.referralCount ?? 0) === Number(mapping.referralCount ?? 0)
        );
      case 'POST_VISIT_HAPPY':
      case 'POST_VISIT_NEUTRAL':
      case 'POST_VISIT_UNHAPPY':
        return !rule.sentiment || rule.sentiment === mapping.sentiment;
      default:
        return true;
    }
  }

  private mapLegacyIncentiveToRule(
    incentive: IIncentive,
  ): LegacyIncentiveRuleMapping | null {
    if (incentive.appliesTo === 'referrals') {
      return {
        triggerType: 'REFERRED_FRIEND_WELCOME',
        goal: 'Welcome the referred friend and offer a reward for visiting',
        sentiment: 'any',
        priority: 5,
        confidence: 0.95,
        reasoning:
          'Legacy incentive applies to referrals, so it maps to the Referred Friend Welcome template.',
      };
    }

    switch (incentive.purpose) {
      case IncentivePurposeTypeEnum.recovery:
        return {
          triggerType: 'POST_VISIT_UNHAPPY',
          goal: 'Apologize and offer to make it right',
          sentiment: 'negative',
          priority: 20,
          confidence: 0.95,
          reasoning:
            'Legacy recovery incentives match the Post Visit Negative template.',
        };
      case IncentivePurposeTypeEnum.retention:
        return {
          triggerType: 'POST_VISIT_NEUTRAL',
          goal: 'Ask what could have gone better and show genuine interest in improving',
          sentiment: 'neutral',
          priority: 15,
          confidence: 0.95,
          reasoning:
            'Legacy retention incentives match the Post Visit Neutral template.',
        };
      case IncentivePurposeTypeEnum.advocate:
        return this.mapAdvocateIncentive(incentive);
      case IncentivePurposeTypeEnum.enrolledCustomer:
        return {
          triggerType: 'ENROLLMENT_REWARD',
          goal: ENROLLMENT_REWARD_GOAL,
          sentiment: 'any',
          priority: 5,
          confidence: 0.95,
          reasoning:
            'Legacy enrolled customer incentives match the Enrollment Reward template.',
        };
      case IncentivePurposeTypeEnum.loyalty:
        return this.mapLoyaltyIncentive(incentive);
      case IncentivePurposeTypeEnum.specialIncentive:
        return {
          triggerType: 'CUSTOM',
          goal: `Send the ${incentive.name} incentive when this custom trigger applies.`,
          sentiment: 'any',
          priority: 0,
          confidence: 0.45,
          reasoning:
            'Legacy special incentives do not include a specific trigger condition, so they remain custom for review.',
        };
      case IncentivePurposeTypeEnum.points:
      case IncentivePurposeTypeEnum.membership:
      default:
        return null;
    }
  }

  private mapAdvocateIncentive(
    incentive: IIncentive,
  ): LegacyIncentiveRuleMapping {
    const requiredReferrals = Number(incentive.requiredReferrals ?? 0);
    if (requiredReferrals > 0) {
      return {
        triggerType: 'REFERRAL_MILESTONE',
        goal: 'Thank customer for referrals and offer a reward',
        sentiment: 'any',
        priority: 5,
        referralCount: requiredReferrals,
        recurring: incentive.triggerType === TriggerType.EVERY_TIME,
        confidence: 0.9,
        reasoning: `Legacy advocate incentive requires ${requiredReferrals} referral(s), so it maps to that Referral Milestone trigger.`,
      };
    }

    return {
      triggerType: 'POST_VISIT_HAPPY',
      goal: 'Thank the customer for their positive experience and show genuine appreciation',
      sentiment: 'positive',
      priority: 10,
      confidence: 0.9,
      reasoning:
        'Legacy advocate incentives without referral thresholds match the Post Visit Happy template.',
    };
  }

  private mapLoyaltyIncentive(
    incentive: IIncentive,
  ): LegacyIncentiveRuleMapping | null {
    const loyaltySelect = Number(incentive.loyaltySelect);
    if (loyaltySelect === LoyaltySelectEnum.amountSpent) {
      const amountSpent = Number(incentive.amountSpent ?? 0);
      if (amountSpent <= 0) return null;
      return {
        triggerType: 'AMOUNT_SPENT',
        goal: 'Thank the customer for their loyalty and reward them for reaching a spending milestone',
        sentiment: 'any',
        priority: 5,
        amountSpentThreshold: amountSpent,
        recurring: incentive.triggerType === TriggerType.EVERY_TIME,
        confidence: 0.95,
        reasoning: `Legacy loyalty incentive uses a ${this.currency(amountSpent)} amount spent threshold, so it maps to that Amount Spent Milestone trigger.`,
      };
    }

    const visitsCount = Number(incentive.visitsCount ?? 0);
    if (loyaltySelect === LoyaltySelectEnum.visitsCount && visitsCount > 0) {
      return {
        triggerType: 'VISIT_MILESTONE',
        goal: 'Celebrate the customer reaching this visit milestone. Acknowledge the visit number, express genuine appreciation for their loyalty, and grant the reward.',
        sentiment: 'any',
        priority: 50,
        visitNumber: visitsCount,
        recurring: incentive.triggerType === TriggerType.EVERY_TIME,
        confidence: 0.95,
        reasoning: `Legacy loyalty incentive uses visit count ${visitsCount}, so it maps to that Visit Milestone trigger.`,
      };
    }

    return null;
  }

  private migrationEntry(incentive: IIncentive): IncentiveMigrationEntry {
    return {
      incentiveId: incentive.id ?? '',
      incentiveName: incentive.name || 'Unnamed incentive',
    };
  }

  private migratedFromIncentiveIdForOffering(
    offering: IOffering,
  ): string | null {
    const migrated = offering as IOffering & {
      migratedFromIncentiveId?: unknown;
    };
    if (typeof migrated.migratedFromIncentiveId === 'string') {
      return migrated.migratedFromIncentiveId;
    }
    return null;
  }

  private isStoreCreditIncentive(incentive: IIncentive): boolean {
    const amount = Number(incentive.amount ?? 0);
    if (
      incentive.type !== 'amount' ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      return false;
    }

    const rewardText = `${incentive.name ?? ''} ${incentive.description ?? ''}`
      .toLowerCase()
      .trim();
    const isExplicitCredit =
      /\b(store|practice|account|loyalty)\s+credit\b/.test(rewardText) ||
      /\bgift\s+card\b/.test(rewardText) ||
      /\bcredit\b/.test(rewardText);
    const isOfferOrService =
      /\b(off|discount|complimentary|free|units?|value|toward)\b/.test(
        rewardText,
      ) || /\bor\b/.test(rewardText);

    return isExplicitCredit && !isOfferOrService;
  }

  private currency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  }

  private buildOfferingLimits(
    incentive: IIncentive,
  ): IOffering['limits'] | undefined {
    const limits: NonNullable<IOffering['limits']> = {};
    if (incentive.triggerType === TriggerType.ONCE) {
      limits.maxRedemptionsPerCustomer = 1;
    }
    if (incentive.startsOn) limits.validFrom = incentive.startsOn;
    if (incentive.expiresOn) limits.validUntil = incentive.expiresOn;
    return Object.keys(limits).length ? limits : undefined;
  }

  private normalizeName(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private stripUndefinedDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.stripUndefinedDeep(item));
    }
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, this.stripUndefinedDeep(item)]),
    );
  }

  /**
   * Redeem a granted offer: updates the grant document (validated, validateInfo)
   * and logs to offersRedemptionLog. Call from the Offer Redemption validate flow.
   */
  async redeemOffer(params: {
    customerId: string;
    grantId: string;
    approvedByPath: string;
  }): Promise<{ ok: boolean }> {
    const callable = this.functions.httpsCallable<
      { customerId: string; grantId: string; approvedByPath: string },
      { ok: boolean }
    >('redeemOffer');
    return firstValueFrom(
      callable(params).pipe(
        catchError((err) => {
          this.sharedService.logErrors(
            err,
            'redeem offer',
            'AiConciergeService',
          );
          throw err;
        }),
      ),
    );
  }
}
